import { useApolloClient } from '@apollo/client';
import { TagSortOrder } from '@mnd-frontend/graphql-schema';
import { Page, PageHeader } from '@mnd-frontend/ui/publish/components';
import { useToasts } from '@mnd-frontend/ui/publish/contexts';
import { purgeImageUsages } from '@mnd-frontend/utils';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { useCurrentPressroom } from '../../contexts/CurrentPressroomContext';
import {
  useTagMoveHighlightedMutation,
  useTagsQuery,
  useUpdateTagMutation,
} from '../../generated/hooks';
import type { TagManagementTagFragment } from '../../generated/types';
import { QUERY_PARAM_FOR_PREFILLED_TAG_SEARCH } from '../../lib/constants';
import CombineTagsModal from './CombineTagsModal';
import DeleteTagsModal from './DeleteTagsModal';
import EditTagModal from './EditTagModal';
import TagDataTable from './TagDataTable';
import TagImageUploadModal from './TagImageUploadModal';

const TAGS_PER_PAGE = 50;

const TagManagement = () => {
  const [t] = useTranslation();
  const { showSuccessToast, showFailureToast } = useToasts();
  const { pressroomId } = useCurrentPressroom();
  const [imageTag, setImageTag] = useState<TagManagementTagFragment | undefined>(undefined);
  const [params] = useSearchParams();

  const [searchQuery, setSearchQuery] = useState(
    () => params.get(QUERY_PARAM_FOR_PREFILLED_TAG_SEARCH) || '',
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedSort, setSelectedSort] = useState(TagSortOrder.Name);

  const { loading, error, data } = useTagsQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      pressroomId,
      query: searchQuery,
      perPage: TAGS_PER_PAGE,
      page: currentPage,
      sortOrder: selectedSort,
    },
  });

  const cache = useApolloClient().cache;
  const [updateTag] = useUpdateTagMutation({
    onCompleted: response => {
      purgeImageUsages(cache, response.tagUpdate?.newImage);
      purgeImageUsages(cache, response.tagUpdate?.previousImage);
    },
  });
  const [moveHighlighted] = useTagMoveHighlightedMutation();

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isCombineTagsOpen, setIsCombineTagsOpen] = useState(false);
  const [isDeleteTagsOpen, setIsDeleteTagsOpen] = useState(false);
  const [openTagId, setOpenTagId] = useState<string | undefined>();

  const items = data?.pressroom?.tags2?.hits ?? [];
  const totalCount = data?.pressroom?.tags2.totalCount ?? 0;
  const totalPages = Math.ceil(totalCount / TAGS_PER_PAGE);

  const toastErrorMessage = error && t('common_general_error');
  useEffect(() => {
    if (toastErrorMessage) {
      showFailureToast(toastErrorMessage);
    }
  }, [showFailureToast, toastErrorMessage]);

  useEffect(() => {
    const total = data?.pressroom?.tags2.totalCount ?? 0;
    const hitLength = data?.pressroom?.tags2.hits.length ?? 0;

    // This is to prevent to show last page with empty result after removing a tag
    if (total > 0 && hitLength === 0 && currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  }, [data, currentPage]);

  const onSelectedIdsChanged = useCallback((ids: any[]) => setSelectedIds(ids), []);
  return (
    <Page loading={loading}>
      <PageHeader
        title={t('category_management_heading')}
        description={t('category_management_subheading')}
      />
      <TagDataTable
        tags={items}
        totalCount={totalCount}
        totalPages={totalPages}
        currentPage={currentPage}
        selectedIds={selectedIds}
        searchQuery={searchQuery}
        pressroomId={pressroomId}
        selectedSort={selectedSort}
        onChangePage={p => {
          setCurrentPage(p);
        }}
        onHighlightClick={async tag => {
          try {
            const newHighlighted = !tag.highlighted;
            const result = await updateTag({
              variables: {
                id: tag.id,
                highlighted: newHighlighted,
                query: searchQuery,
                perPage: TAGS_PER_PAGE,
                page: currentPage,
                sortOrder: selectedSort,
                pressroomId,
              },
            });
            const error = result.data?.tagUpdate?.error;
            if (!error) {
              showSuccessToast(
                newHighlighted
                  ? t('category_management_highlight_success', { categoryName: tag.name })
                  : t('category_management_unhighlight_success', { categoryName: tag.name }),
              );
            } else {
              showFailureToast(error?.message ?? t('common_general_error'));
            }
          } catch (_err) {
            showFailureToast(t('common_general_error'));
          }
        }}
        onEditTagClick={tag => setOpenTagId(tag.id)}
        onSelectImage={tag => setImageTag(tag)}
        onMoveHighlightedTag={async (tag, above) => {
          try {
            const result = await moveHighlighted({
              variables: {
                id: tag.id,
                relativeTo: tag.id,
                above,
                query: searchQuery,
                page: currentPage,
                perPage: TAGS_PER_PAGE,
                sortOrder: selectedSort,
                pressroomId,
              },
            });
            const error = result.data?.tagMoveHighlighted?.error;
            if (error) {
              showFailureToast(error?.message ?? t('common_general_error'));
            }
          } catch (_err) {
            showFailureToast(t('common_general_error'));
          }
        }}
        onSelectedIdsChanged={onSelectedIdsChanged}
        onCombineClick={() => setIsCombineTagsOpen(true)}
        onDeleteClick={() => setIsDeleteTagsOpen(true)}
        onSearchQueryChanged={query => {
          setCurrentPage(1);
          setSearchQuery(query);
        }}
        onSelectedSortChanged={sort => {
          if (!sort) return;
          setSelectedSort(sort);
        }}
      />
      {isCombineTagsOpen ? (
        <CombineTagsModal
          page={currentPage}
          perPage={TAGS_PER_PAGE}
          sortOrder={selectedSort}
          query={searchQuery}
          onClose={() => {
            setIsCombineTagsOpen(false);
          }}
          onSave={() => {
            setIsCombineTagsOpen(false);
            setSelectedIds([]);
          }}
          tagIds={selectedIds}
          pressroomId={pressroomId}
        />
      ) : null}
      {openTagId ? (
        <EditTagModal
          id={openTagId}
          pressroomId={pressroomId}
          query={searchQuery}
          perPage={TAGS_PER_PAGE}
          page={currentPage}
          sortOrder={selectedSort}
          onClose={() => setOpenTagId(undefined)}
        />
      ) : null}
      {isDeleteTagsOpen ? (
        <DeleteTagsModal
          tagIds={selectedIds}
          pressroomId={pressroomId}
          query={searchQuery}
          perPage={TAGS_PER_PAGE}
          page={currentPage}
          sortOrder={selectedSort}
          onClose={() => {
            setIsDeleteTagsOpen(false);
          }}
          onSave={() => {
            setIsDeleteTagsOpen(false);
            setSelectedIds([]);
          }}
        />
      ) : null}
      {imageTag ? (
        <TagImageUploadModal
          tag={imageTag}
          onClose={() => setImageTag(undefined)}
          query={searchQuery}
          perPage={TAGS_PER_PAGE}
          page={currentPage}
          sortOrder={selectedSort}
        />
      ) : null}
    </Page>
  );
};

export default TagManagement;
